import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC } from '@lyrahealth-inc/shared-app-logic/src/features/centerOfExcellence/types'
import { PAGE_ROUTES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import { SEARCHER_TYPE_TO_MATCH_FOR } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'
import { useSearchAgeAndType } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/hooks/useSearchAgeAndType'
import {
  TOOLKIT_TYPE,
  WELLNESS_TOOLKIT_PROMOTION,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessToolkit/types'
import { ProviderProgramTaxonomy } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { useGetUserDisplayName } from './useGetUserDisplayName'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import { getSelfSearchAvailable, getUserAge, getUserDOB } from '../../data/user/userSelectors'
import { handleZeroViewSelect } from '../../features/appointmentDashboard/data/appointmentDashboardActions'
import { setToolkitType } from '../../features/onboard/data/onboardDataActions'
import {
  clearProgramPreference,
  setIsSearchToolkitAware,
  setProgramPreference,
  setSearcherInfo,
  setToolkitSubTopic,
} from '../../features/triage/data/triageActions'

export const useNavigateToSearchForCare = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { firstName, lastName } = useGetUserDisplayName()
  const userAge = useSelector(getUserAge)
  const userDOB = useSelector(getUserDOB)
  const getSearchType = useSearchAgeAndType()
  const selfSearchAvailable = useSelector(getSelfSearchAvailable)

  return useCallback(
    (
      args: Partial<{
        programPreference: ProviderProgramTaxonomy
        toolkitType: TOOLKIT_TYPE
        showSummaryView?: boolean
        toolkitSubTopic?: TOOLKIT_CENTER_OF_EXCELLENCE_SUB_TOPIC
        isSearchToolkitAware?: boolean
      }> = {},
    ) => {
      if (!selfSearchAvailable) {
        // Assumes searcher type to match for is SEARCHER_TYPE_TO_MATCH_FOR.SELF
        navigate(PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE)
        return
      }
      if (userAge === undefined) {
        console.warn('Failed to navigate to search for care - missing user data')
        return
      }
      // For Critical Incident Support (COE), ensure any stale programPreference is cleared
      if (args.toolkitType === WELLNESS_TOOLKIT_PROMOTION.TRAUMA) {
        dispatch(clearProgramPreference())
      }

      dispatch(setIsSearchToolkitAware(Boolean(args.isSearchToolkitAware)))

      dispatch(
        handleZeroViewSelect({
          whoAmIHereFor: SEARCHER_TYPE_TO_MATCH_FOR.SELF,
          navigate,
          currentLocation: location.pathname,
          showSummaryView: !!args.showSummaryView,
        }),
      )
      if (args.programPreference) {
        dispatch(setProgramPreference(args.programPreference))
      }
      if (args.toolkitType) {
        dispatch(setToolkitType(args.toolkitType))
      }
      if (args.toolkitSubTopic) {
        dispatch(setToolkitSubTopic(args.toolkitSubTopic))
      }
      const { type } = getSearchType({ dob: userDOB, matchFor: SEARCHER_TYPE_TO_MATCH_FOR.SELF })
      dispatch(
        setSearcherInfo({
          name: firstName,
          age: userAge,
          type,
          dob: userDOB,
          lastName,
        }),
      )
    },
    [selfSearchAvailable, userAge, dispatch, navigate, getSearchType, userDOB, firstName, lastName],
  )
}
